import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X, SlidersHorizontal } from 'lucide-react';
import { Button, Dialog, Select } from '@easymoving/ui';
import type { Destination, Item } from '@easymoving/types';
import { roomOptions } from '../../lib/taxonomy/options';
import { DestinationSelect } from './QuickSelects';
import { useBoxes, useCustomRooms } from '../../hooks/useUserData';

const NONE = '__none__';
const KEEP = '__keep__'; // "no change" (Radix forbids an empty-string item value)
const NO_BOX = '__nobox__';

type Patch = Partial<Pick<Item, 'destination' | 'room' | 'boxId'>>;

/**
 * Sticky action bar for the gallery's list view when items are selected.
 * "Edit" opens a sheet to set destination/room/box on all of them at once;
 * delete is guarded by a confirm (bulk deletes have no undo).
 */
export function BulkBar({
  count,
  totalVisible,
  onSelectAll,
  onClear,
  onApply,
  onDelete,
}: {
  count: number;
  totalVisible: number;
  onSelectAll: () => void;
  onClear: () => void;
  onApply: (patch: Patch) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const customRooms = useCustomRooms();
  const { boxes } = useBoxes();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dest, setDest] = useState<string>(KEEP);
  const [room, setRoom] = useState<string>(KEEP);
  const [box, setBox] = useState<string>(KEEP);

  function reset() {
    setDest(KEEP);
    setRoom(KEEP);
    setBox(KEEP);
  }

  function apply() {
    const patch: Patch = {};
    if (dest !== KEEP) patch.destination = dest as Destination;
    if (room !== KEEP) patch.room = room === NONE ? undefined : room;
    if (box !== KEEP) patch.boxId = box === NO_BOX ? undefined : box;
    if (Object.keys(patch).length > 0) onApply(patch);
    setEditOpen(false);
    reset();
  }

  const roomOpts = [
    { value: KEEP, label: t('bulk.noChange') },
    { value: NONE, label: t('bulk.clear') },
    ...roomOptions(t, customRooms),
  ];
  const boxOpts = [
    { value: KEEP, label: t('bulk.noChange') },
    { value: NO_BOX, label: t('item.noBox') },
    ...boxes.map((b) => ({ value: b.id, label: b.name })),
  ];

  return (
    <>
      <div className="fixed inset-x-0 bottom-16 md:bottom-0 z-30 border-t hairline bg-bg/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-[640px] px-4 py-2.5 flex items-center gap-3">
          <button
            type="button"
            onClick={onClear}
            aria-label={t('bulk.clearSelection')}
            className="size-8 rounded-md flex items-center justify-center hover:bg-surface-tint shrink-0"
          >
            <X className="size-4" />
          </button>
          <span className="text-[14px] font-medium">{t('bulk.selected', { count })}</span>
          {count < totalVisible && (
            <button type="button" onClick={onSelectAll} className="text-petrol text-[13px] font-medium">
              {t('bulk.selectAll')}
            </button>
          )}
          <div className="ms-auto flex items-center gap-2">
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <SlidersHorizontal className="size-4" />
              {t('bulk.edit')}
            </Button>
            <Button variant="ghost" className="text-clay" onClick={() => setConfirmDelete(true)} aria-label={t('item.delete')}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        open={editOpen}
        onOpenChange={(o) => {
          setEditOpen(o);
          if (!o) reset();
        }}
        title={t('bulk.editTitle', { count })}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditOpen(false)}>
              {t('item.cancel')}
            </Button>
            <Button onClick={apply}>{t('bulk.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3">
          <Field label={t('item.destination')}>
            <DestinationSelect value={dest} lead={[{ value: KEEP, label: t('bulk.noChange') }]} onValueChange={setDest} />
          </Field>
          <Field label={t('item.room')}>
            <Select ariaLabel={t('item.room')} value={room} options={roomOpts} onValueChange={setRoom} />
          </Field>
          <Field label={t('item.box')}>
            <Select ariaLabel={t('item.box')} value={box} options={boxOpts} onValueChange={setBox} />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('bulk.deleteTitle', { count })}
        description={t('bulk.deleteBody')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('item.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            >
              {t('item.delete')}
            </Button>
          </div>
        }
      >
        <span className="sr-only">{t('bulk.deleteBody')}</span>
      </Dialog>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[13px] font-medium tracking-snug">
      <span className="text-ink">{label}</span>
      {children}
    </label>
  );
}
